import React, {
    memo, useEffect, useState,
} from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Icon, TextInputHelperCurrency } from '@honeycomb-npm/honeycomb-react';
import { DynamicContent } from '@commblades/commblades';

import {
    AmountContainer, AmountWrapper, DescriptionWrapper, SliderWrapper,
} from './AmountSlider.styled';
import formatCurrency from '../../utils/formatCurrency';
import { BORROWING_POWER_EDIT_BTN_LABEL, BORROWING_POWER_EDIT_BTN_SPAN_LABEL } from '../../app/navigation/types';

export type SliderInputValidations = {
    defaultErrorMessage?: string;
    minValueMessage?: string;
    minValue?: number;
    maxValueMessage?: string;
    maxValue?: number;
};

interface SliderProps {
    minValue: number;
    middleValue?: number;
    maxValue: number;
    currentValue: number;
    steps?: number;
    header?: string;
    description?: string;
    descriptionParas?: string[];
    validations?: SliderInputValidations;
    onChange: (value: number) => void;
    onErrorHandle: (error: string) => void;
    forwardedRef: React.Ref<HTMLInputElement>;
}

const AmountSlider: React.FC<SliderProps> = memo(({
    minValue,
    middleValue,
    maxValue,
    currentValue,
    steps,
    header,
    description,
    descriptionParas,
    validations,
    onChange,
    onErrorHandle,
    forwardedRef,
}) => {
    const [amount, setAmount] = useState(currentValue);
    const [editAmount, setEditAmount] = useState(maxValue);
    const [showEdit, setShowEdit] = useState(true);

    const toggleEdit = () => {
        setShowEdit(!showEdit);
    };

    const handleEditAmountInputChange = (name: string, value: string) => {
        setEditAmount(Number(value));
    };

    const handleEditAmountInputBlur = () => {
        if (editAmount >= minValue && editAmount <= maxValue) {
            setAmount(editAmount);
            onChange(editAmount);
            onErrorHandle('');
            toggleEdit();
        } else {
            onErrorHandle('Please enter a valid amount.');
        }
    };

    const srOnlyMessage = `Enter an amount, minimum ${formatCurrency(minValue)} and maximum ${formatCurrency(maxValue)}`;

    useEffect(() => {
        if (!showEdit) {
            (forwardedRef as React.RefObject<HTMLInputElement>)?.current?.focus();
        }
    }, [showEdit]);

    useEffect(() => {
        setAmount(maxValue);
        setEditAmount(maxValue);
    }, [maxValue]);

    useEffect(() => {
        setAmount(currentValue);
        setEditAmount(currentValue);
    }, [currentValue]);

    return (
        <>
            {header && (
                <legend>
                    <h3 id="amount-slider-heading" className="h3">
                        <DynamicContent content={header} id="amount-slider-heading-content" />
                    </h3>
                </legend>
            )}
            <AmountContainer>
                <AmountWrapper>
                    {showEdit && <div id="amount-slider-label">{`$${formatCurrency(amount)}`}</div>}
                    {!showEdit && (
                        <><span className="sr-only">{srOnlyMessage}</span><TextInputHelperCurrency
                            id="amount-edit-input"
                            data-testid="amount-edit-input"
                            name="amount-edit-input"
                            label=""
                            value={editAmount > 0 ? formatCurrency(editAmount) : ''}
                            min={minValue}
                            max={maxValue}
                            errormessage={validations?.defaultErrorMessage || 'Please enter a valid amount.'}
                            errormessageMin={validations?.minValueMessage || `Minimum amount is $${formatCurrency(minValue)}`}
                            errormessageMax={validations?.maxValueMessage || `Maximum amount is $${formatCurrency(maxValue)}`}
                            required
                            onChange={handleEditAmountInputChange}
                            onBlur={handleEditAmountInputBlur}
                            forwardedRef={forwardedRef}
                            inputMode="decimal"
                            autoComplete="off"
                        />
                        </>
                    )}
                </AmountWrapper>
                {showEdit && (
                    <>
                        <span className="sr-only">{BORROWING_POWER_EDIT_BTN_SPAN_LABEL}</span>
                        <Icon
                            id="amount-edit-button"
                            data-testid="amount-edit-button"
                            symbol="controlEdit24"
                            isRHS
                            onClick={toggleEdit}
                            screenreader="borrowing amount"
                        >
                            {BORROWING_POWER_EDIT_BTN_LABEL}
                        </Icon>
                    </>
                )}
            </AmountContainer>
            <SliderWrapper id="amount-edit-slider">
                <Slider
                    aria-hidden="true"
                    min={minValue}
                    max={maxValue}
                    value={amount}
                    styles={{
                        tracks: { backgroundColor: '#1175B5' },
                        track: { backgroundColor: '#1175B5' },
                        handle: {
                            backgroundColor: '#1175B5',
                            height: 20,
                            width: 20,
                            marginTop: -9,
                            border: 'none',
                        },
                        rail: { backgroundColor: '#4A575D' },
                    }}
                    marks={middleValue ? {
                        [minValue]: {
                            label: `$${formatCurrency(minValue)}`,
                            style: {
                                fontSize: '0.85rem',
                                fontWeight: 'normal',
                                color: '#1E1E1E',
                                paddingLeft: '2.5rem',
                            },
                        },
                        [middleValue]: {
                            label: `$${formatCurrency(middleValue)}`,
                            style: {
                                fontSize: '0.85rem',
                                fontWeight: 'normal',
                                color: '#1E1E1E',
                                paddingLeft: '2.5rem',
                            },
                        },
                        [maxValue]: {
                            label: `$${formatCurrency(maxValue)}`,
                            style: {
                                fontSize: '0.85rem',
                                fontWeight: 'normal',
                                color: '#1E1E1E',
                                paddingRight: '2.5rem',
                            },
                        },
                    } : {
                        [minValue]: {
                            label: `$${formatCurrency(minValue)}`,
                            style: {
                                fontSize: '0.85rem',
                                fontWeight: 'normal',
                                color: '#1E1E1E',
                                paddingLeft: '2.5rem',
                            },
                        },
                        [maxValue]: {
                            label: `$${formatCurrency(maxValue)}`,
                            style: {
                                fontSize: '0.85rem',
                                fontWeight: 'normal',
                                color: '#1E1E1E',
                                paddingRight: '2.5rem',
                            },
                        },
                    }}
                    step={steps || 100}
                    onChange={(values: number | number[]) => {
                        setEditAmount(values as number);
                        setAmount(values as number);
                        onChange(values as number);
                        setShowEdit(true);
                        onErrorHandle('');
                    }}
                />
            </SliderWrapper>
            {description && (
                <DescriptionWrapper>
                    <DynamicContent
                        content={description}
                        params={descriptionParas}
                        id="amount-slider-description"
                        className="mt-5"
                        isParagraph
                    />
                </DescriptionWrapper>
            )}
        </>
    );
});

export default AmountSlider;
